/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { ISchemaRouteRefModel, SCHEMA_VALUE_TYPE, TGenericModelNode } from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { InspectModel } from "../InspectModel";
import { FieldRow } from "../../Fields/FieldRow";
import { FieldNote } from "../../Fields/FieldNote";
import { Icon, ICON_SIZE } from "../../Icon";

export const ControlRouteRef_Children: TInspectorControlComponent<ISchemaRouteRefModel> = ({
	model: _model,
	path,
	depth
}) => {
	const { model } = useBlueprintModel(_model, "routeRef");
	const { model: routeNameModel } = useBlueprintModel(_model?.routeName);
	const { model: paramsModel } = useBlueprintModel(_model?.params);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			<InspectModel model={routeNameModel} path={path.concat([ "routeName" ])} depth={depth} />
			{paramsModel?.value ? (
				<InspectModel model={paramsModel.value} path={path.concat([ "params" ])} depth={depth + 1} />
			) : routeNameModel.type === SCHEMA_VALUE_TYPE.CONST && routeNameModel.constant.value ? (
				<FieldRow>
					<FieldNote color="warning" padded>
						<Icon name="mdi/alert" size={ICON_SIZE.XSMALL} />
						{/* @todo Translation */}
						<span>Route not found.</span>
					</FieldNote>
				</FieldRow>
			) : null}
		</>
	);
};

export const ControlRouteRef = (model: TGenericModelNode): IInspectorControls => ({
	children: {
		component: ControlRouteRef_Children,
		model: model
	}
});
